import { useCallback } from 'react';
import type { ExternalTableSchema } from '@packages/table-builder';
import { DynamicTableV2 } from '@packages/table-builder';
import { Section } from '@packages/ui/layout';
import { EmptyState } from '@packages/ui/layout/EmptyState';
import { axiosInstance } from '@/services/apiInterceptor';
import type { TableBlock, BlockRendererSharedProps } from '../types/blocks';
import { getTableLoader } from '../registry/loaderRegistry';
import { BlockLoadState } from './BlockLoadState';
import { useAsyncBlockResource } from './useAsyncBlockResource';

export interface TableBlockRendererProps extends BlockRendererSharedProps {
  block: TableBlock;
}

export function TableBlockRenderer({ block }: TableBlockRendererProps): JSX.Element {
  // Pre-fetched data from a builder action — render static table
  if (block.data != null) {
    return (
      <Section title={block.title} semanticId={block.semanticId}>
        <StaticTable data={block.data} columns={block.columns} />
      </Section>
    );
  }

  const isUnconfigured = !block.schema;

  const loader = useCallback(async () => {
    const tableLoader = getTableLoader();
    if (!tableLoader) {
      throw new Error(
        `[TableBlockRenderer] No table loader registered. ` +
        `Ensure "@apps/customer/tableSchemaLoader" is imported before rendering.`,
      );
    }

    const schema = (await tableLoader(block.schema!)) ?? (await tableLoader(block.$id));
    if (!schema) {
      throw new Error(
        `[TableBlockRenderer] No table schema found for "${block.schema}" ` +
        `(block $id: "${block.$id}"). ` +
        `Check that a matching page schema.ts exists in src/apps/customer/pages/.`,
      );
    }

    return schema as ExternalTableSchema;
  }, [block.$id, block.schema]);

  const resource = useAsyncBlockResource(
    isUnconfigured ? '__skip__' : `table-block:${block.$id}`,
    loader,
  );

  if (isUnconfigured) {
    return (
      <EmptyState
        icon='table_chart'
        title='Table block not configured'
        description='Select this block in the inspector and set the table schema.'
        semanticId='tableBlockUnconfigured'
      />
    );
  }

  const isLoading = resource.status === 'idle' || resource.status === 'loading';
  const error = resource.status === 'error' ? resource.error : null;

  return (
    <BlockLoadState isLoading={isLoading} error={error}>
      {resource.status === 'ready' && (
        <Section title={block.title} semanticId={block.semanticId}>
          <DynamicTableV2
            schema={resource.data}
            namespace={block.namespace!}
            axiosInstance={axiosInstance as never}
            semanticId={block.semanticId}
            context={block.context}
          />
        </Section>
      )}
    </BlockLoadState>
  );
}

function StaticTable({ data, columns }: { data: unknown[]; columns?: Array<{ key: string; label: string; [k: string]: unknown }> }) {
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon='table_chart'
        title='No data'
        description='No records to display.'
        semanticId='staticTableEmpty'
      />
    );
  }

  const cols = columns ?? Object.keys(data[0] as Record<string, unknown>).map((key) => ({
    key,
    label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
  }));

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {cols.map((col) => (
              <th
                key={col.key}
                style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151' }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              {cols.map((col) => (
                <td
                  key={col.key}
                  style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', color: '#4b5563' }}
                >
                  {String((row as Record<string, unknown>)[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
